import { useCallback, useEffect, useReducer, useRef } from 'react';

/**
 * Windowing for the continuous journal's day stack (audit F6). Paging back through the journal used
 * to leave every visited day's FULL editor subtree mounted — ~50 live TipTap editors, Y.Docs,
 * IndexedDB persistences and sync providers after five screens of scrolling. This hook watches each
 * day <section> with one shared IntersectionObserver (±`marginViewports` viewport heights of slack)
 * and tells the caller which sections should render their live editor and which should render a
 * fixed-height placeholder instead:
 *
 * - A section that leaves the margin is "parked": its body height is measured at that moment (while
 *   the DOM is still live) and cached per key, so the placeholder occupies exactly the same space —
 *   scroll position never jumps. The editor subtree unmounts through its normal teardown (editors.tsx
 *   destroys the editor/provider/Y.Doc; DaySignals' cleanup is already correct).
 * - A section re-entering the margin remounts; the editor rehydrates from IndexedDB. Until the
 *   caller reports hydration (`onHydrated`, wired to the editor's first content report), the body
 *   should hold the cached height as a min-height so the async rehydration can't collapse the
 *   section and yank the scroll position (Safari has no scroll anchoring).
 * - Keys in `alwaysLive` (today + the anchor day) are never parked.
 *
 * DOM stays overflow-anchor friendly: stable section keys, the same body element position, and a
 * height-preserving placeholder.
 */
export interface DayWindowSection {
  /** Attach to the day's <section> element (the observed root of the window test). */
  sectionRef: (node: HTMLElement | null) => void;
  /** Attach to the measurable body wrapper (editor + signals) while live. */
  bodyRef: (node: HTMLElement | null) => void;
  /** Render the live editor subtree when true; the fixed-height placeholder when false. */
  live: boolean;
  /** Last measured body height (px) — the placeholder's height. Null before first measurement. */
  height: number | null;
  /** Min-height to hold while a remounted editor rehydrates; null once hydrated (or never parked). */
  holdHeight: number | null;
}

export interface DayWindow {
  /** Per-key render state; safe to call during render (stable ref callbacks per key). */
  section: (key: string) => DayWindowSection;
  /** Report that a remounted day's editor has content again (clears the hold min-height). */
  onHydrated: (key: string) => void;
}

/** How much viewport-height slack a section gets on each side before its editor is parked. */
const DEFAULT_MARGIN_VIEWPORTS = 2;

export function useDayWindow(
  alwaysLive: readonly string[],
  marginViewports: number = DEFAULT_MARGIN_VIEWPORTS,
): DayWindow {
  // All bookkeeping lives in refs (the IO callback and render both need current values without
  // stale-closure hazards); `bump` re-renders the consumer whenever the window state changes.
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const parkedRef = useRef(new Set<string>());
  const holdingRef = useRef(new Set<string>());
  const heightsRef = useRef(new Map<string, number>());
  const sectionElsRef = useRef(new Map<string, HTMLElement>());
  const bodyElsRef = useRef(new Map<string, HTMLElement>());
  const keysByElRef = useRef(new Map<Element, string>());
  const refFnsRef = useRef(new Map<string, { sectionRef: (node: HTMLElement | null) => void; bodyRef: (node: HTMLElement | null) => void }>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const alwaysLiveRef = useRef(alwaysLive);
  alwaysLiveRef.current = alwaysLive;

  const getObserver = useCallback((): IntersectionObserver | null => {
    if (observerRef.current) return observerRef.current;
    // No IntersectionObserver (jsdom without a stub) → windowing is off; everything stays live.
    if (typeof IntersectionObserver === 'undefined') return null;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        let changed = false;
        for (const entry of entries) {
          const key = keysByElRef.current.get(entry.target);
          if (!key) continue;
          if (entry.isIntersecting) {
            if (!parkedRef.current.has(key)) continue;
            parkedRef.current.delete(key);
            // Hold the cached height while the editor rehydrates from IndexedDB.
            if (heightsRef.current.has(key)) holdingRef.current.add(key);
            changed = true;
          } else {
            if (parkedRef.current.has(key) || alwaysLiveRef.current.includes(key)) continue;
            // Measure BEFORE unmounting — the placeholder must occupy exactly this space.
            const body = bodyElsRef.current.get(key);
            if (body) heightsRef.current.set(key, body.offsetHeight);
            parkedRef.current.add(key);
            holdingRef.current.delete(key);
            changed = true;
          }
        }
        if (changed) bump();
      },
      // ±N viewport heights of slack around the (viewport) root — percentages track resizes free.
      { rootMargin: `${marginViewports * 100}% 0px` },
    );
    return observerRef.current;
  }, [marginViewports]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const section = useCallback(
    (key: string): DayWindowSection => {
      let fns = refFnsRef.current.get(key);
      if (!fns) {
        const sectionRef = (node: HTMLElement | null): void => {
          const prev = sectionElsRef.current.get(key);
          if (prev && prev !== node) {
            observerRef.current?.unobserve(prev);
            keysByElRef.current.delete(prev);
          }
          if (node) {
            sectionElsRef.current.set(key, node);
            keysByElRef.current.set(node, key);
            getObserver()?.observe(node);
          } else {
            sectionElsRef.current.delete(key);
          }
        };
        const bodyRef = (node: HTMLElement | null): void => {
          if (node) bodyElsRef.current.set(key, node);
          else bodyElsRef.current.delete(key);
        };
        fns = { sectionRef, bodyRef };
        refFnsRef.current.set(key, fns);
      }
      const live = alwaysLive.includes(key) || !parkedRef.current.has(key);
      const height = heightsRef.current.get(key) ?? null;
      return {
        sectionRef: fns.sectionRef,
        bodyRef: fns.bodyRef,
        live,
        height,
        holdHeight: live && holdingRef.current.has(key) ? height : null,
      };
    },
    [alwaysLive, getObserver],
  );

  const onHydrated = useCallback((key: string): void => {
    // Hot path (called on every editor content report) — bail without state churn when idle.
    if (!holdingRef.current.has(key)) return;
    holdingRef.current.delete(key);
    bump();
  }, []);

  return { section, onHydrated };
}
